import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';
import { Box, Typography, Paper, Grid, Snackbar, Alert } from '@mui/material';
import ColorPalette from '../../../colorpalette/ColorPalette';
import { SendWhatsAppMessage } from '../../../api/WhatsappAPI';
import MessagesTemplate from '../../../consts/MessagesTemplate';
import { useWhatsAppSender } from '../../../contexts/WhatsAppSenderContext';
import WhatsAppOriginBanner from '../../../components/whatsapp/WhatsAppOriginBanner';
import { isWhatsAppPolicyError, getWhatsAppErrorMessage } from '../../../utils/whatsappErrors';
import usePlanLock from '../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../consts/SubscriptionFeatures';
import { BUSINESS_PLAN_NAME } from '../../../components/subscription/planLockTexts';

// Import existing components
import RecipientSelector from './RecipientSelector';
import MessageComposer from './MessageComposer';
import RecipientList from './RecipientList';
import SendingMethodSelector from './SendingMethodSelector';

// Import new components
import {
  MassMessagingHeader,
  TabNavigation,
  SelectedRecipientsTable,
  SendingSummary,
  ConfirmationDialog,
  ResultsDialog,
  NavigationButtons,
  LoadingIndicator
} from './components';

const MassMessaging = () => {
  const dataProvider = DataProviderInstance();
  const { capabilities } = useWhatsAppSender();
  // Cadeado por plano (Business): a tela continua navegável; enviar abre o
  // modal "Falar com a gente". Quando o plano bloqueia, o aviso do número
  // próprio (WhatsAppOriginBanner) dá lugar ao do plano.
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.MARKETING, 'A mensagem em massa', BUSINESS_PLAN_NAME);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [messageVariables, setMessageVariables] = useState({
    doctor_name: '',
    company_name: ''
  });
  // Always default to whatsapp_api since email is blocked
  const [sendingMethod, setSendingMethod] = useState('whatsapp_api');
  const [sending, setSending] = useState(false);
  const [sendingResults, setSendingResults] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  // Add state to track the current query type
  const [currentQueryType, setCurrentQueryType] = useState(null);
  const [sendPolicyError, setSendPolicyError] = useState('');

  // Só é possível enviar via WhatsApp API quando há credenciais salvas E a
  // política do número (plataforma × próprio) permite envio em massa
  const whatsappSendingEnabled = whatsappConfigured && capabilities.bulk;

  // Get company data for variables
  useEffect(() => {
    if (dataProvider.CompanyConfig) {
      setMessageVariables(prev => ({
        ...prev,
        company_name: dataProvider.CompanyConfig.Name || ''
      }));
    }
    
    // Check if WhatsApp is configured
    dataProvider.GetWhatsappConfig((result) => {
      if (result && result.config) {
        const hasTokens = result.config.instance_id && result.config.has_instance_token;
        setWhatsappConfigured(hasTokens);
      } else {
        setWhatsappConfigured(false);
      }
    });
  }, [dataProvider.CompanyConfig, dataProvider.GetWhatsappConfig]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle recipient selection
  const handleRecipientsSelected = (newRecipients, queryType = null) => {
    setRecipients(newRecipients);
    // Only select the recipients that were explicitly selected in the RecipientSelector
    // Don't auto-select all recipients
    if (newRecipients.length > 0) {
      setSelectedRecipients(newRecipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
    
    // Update the current query type when recipients are selected from a predefined query
    if (queryType) {
      setCurrentQueryType(queryType);
    }
  };

  // Handle recipient selection change
  const handleRecipientSelectionChange = (recipientId, selected) => {
    if (selected) {
      setSelectedRecipients(prev => [...prev, recipientId]);
    } else {
      setSelectedRecipients(prev => prev.filter(id => id != recipientId));
    }
  };

  // Handle select all recipients
  const handleSelectAllRecipients = (selected, validRecipientIds) => {
    if (selected) {
      if (validRecipientIds) {
        // Use the provided valid recipient IDs
        setSelectedRecipients(validRecipientIds);
      } else {
        // If no valid IDs provided, select all (legacy behavior)
        setSelectedRecipients(recipients.map(r => r.id));
      }
    } else {
      setSelectedRecipients([]);
    }
  };

  // Handle message template change
  const handleMessageTemplateChange = (template) => {
    setMessageTemplate(template);
  };

  // Handle message variables change
  const handleMessageVariablesChange = (variables) => {
    setMessageVariables(variables);
  };

  // Handle sending method change - block email method
  const handleSendingMethodChange = (method) => {
    // Only allow whatsapp_api method, block email
    if (method != 'email') {
      setSendingMethod(method);
    }
  };

  // Handle send to all
  const handleSendToAll = () => {
    if (lock.locked) { lock.openUpgrade(); return; }
    if (messageTemplate.trim() == '') {
      alert('Por favor, insira uma mensagem para enviar.');
      return;
    }

    if (selectedRecipients.length == 0) {
      alert('Por favor, selecione pelo menos um destinatário.');
      return;
    }

    // Show confirmation popup
    setShowConfirmPopup(true);
  };

  // Handle actual sending after confirmation
  const handleConfirmSend = () => {
    setSending(true);
    setSendingResults(null);
    setSendPolicyError('');
    setShowConfirmPopup(false);

    // Prepare recipients data
    const recipientsToSend = recipients
      .filter(r => selectedRecipients.includes(r.id))
      .map(r => ({
        id: r.id,
        name: r.name,
        phone: r.phone,
        email: r.email,
        type: r.type || 'patient',
        appointment_date: r.appointment_date,
        appointment_time: r.appointment_time
      }));

    // Use the selected sending method
    dataProvider.SendMassMessage(
      recipientsToSend,
      messageTemplate,
      messageVariables,
      sendingMethod, // Use the selected sending method (whatsapp_api or email)
      currentQueryType, // Pass the query type to track message history
      (result) => {
        setSending(false);

        // Erro de política do número (plataforma × próprio): não é um
        // resultado de envio (sem summary), então não abre o popup de
        // resultados nem desmarca os destinatários selecionados
        if (result && result.success === false && isWhatsAppPolicyError(result)) {
          setSendPolicyError(getWhatsAppErrorMessage(result));
          return;
        }

        setSendingResults(result);
        setShowResultsPopup(true);
        // Deselect all recipients after sending to prevent accidental resending
        setSelectedRecipients([]);
      }
    );
  };

  // Process message template for a specific recipient
  const processMessageForRecipient = (template, variables, recipient) => {
    let message = template;
    
    // Replace variables with recipient values
    const replacements = {
      '{name}': recipient.name ? recipient.name.split(' ')[0] : '',
      '{fullName}': recipient.name || '',
      '{greetings}': getTimeBasedGreeting(),
      '{doctor_name}': variables.doctor_name || '',
      '{company_name}': variables.company_name || '',
    };
    
    // Add appointment-specific variables if available
    if (recipient.appointment_date) {
      replacements['{appointment_date}'] = formatDate(recipient.appointment_date);
    }
    
    if (recipient.appointment_time) {
      replacements['{appointment_time}'] = formatTime(recipient.appointment_time);
    }
    
    // Replace all variables in the template
    Object.entries(replacements).forEach(([variable, value]) => {
      message = message.replace(new RegExp(variable, 'g'), value);
    });
    
    return message;
  };

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Bom dia";
    } else if (hour >= 12 && hour < 18) {
      return "Boa tarde";
    } else {
      return "Boa noite";
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    
    const parts = date.split('-');
    if (parts.length != 3) return date;
    
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    
    const parts = time.split(':');
    if (parts.length < 2) return time;
    
    return `${parts[0]}:${parts[1]}`;
  };

  // Handle send to individual recipient
  const handleSendToRecipient = (recipientId) => {
    if (lock.locked) { lock.openUpgrade(); return; }
    if (messageTemplate.trim() == '') {
      alert('Por favor, insira uma mensagem para enviar.');
      return;
    }

    // Find recipient
    const recipient = recipients.find(r => r.id == recipientId);
    if (!recipient) return;

    // For individual messages, always use WhatsApp Web
    try {
      // Process message for this recipient
      let processedMessage = processMessageForRecipient(messageTemplate, messageVariables, recipient);
      
      // Send via WhatsApp Web
      SendWhatsAppMessage(recipient.phone, MessagesTemplate.ConvertToWhatsAppText(processedMessage), recipient.countryCode);
      
      // You could update a status for this specific recipient
      
    } catch (error) {
      
    }
  };

  return (
    <Box sx={{ padding: '20px' }}>
      {lock.locked ? <Box sx={{ mb: 2 }}>{lock.banner}</Box> : <WhatsAppOriginBanner context="bulk" />}
      {lock.modal}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: ColorPalette.white,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
          border: `1px solid ${ColorPalette.border}`
        }}
      >
        {/* Header */}
        <MassMessagingHeader />
        
        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} handleTabChange={handleTabChange} />

        {/* Tab 1: Select Recipients */}
        {activeTab == 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" gutterBottom>
                  Contatos Disponíveis
                </Typography>
                <RecipientSelector
                  onRecipientsSelected={handleRecipientsSelected}
                  loading={loading}
                  setLoading={setLoading}
                  selectedRecipients={selectedRecipients}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <SelectedRecipientsTable
                selectedRecipients={selectedRecipients}
                recipients={recipients}
                handleRecipientSelectionChange={handleRecipientSelectionChange}
                handleSelectAllRecipients={handleSelectAllRecipients}
              />
            </Grid>
          </Grid>
        )}

        {/* Tab 2: Compose Message */}
        {activeTab == 1 && (
          <Box>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: ColorPalette.white,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
                border: `1px solid ${ColorPalette.border}`
              }}
            >
              <MessageComposer
                messageTemplate={messageTemplate}
                messageVariables={messageVariables}
                onMessageTemplateChange={handleMessageTemplateChange}
                onMessageVariablesChange={handleMessageVariablesChange}
                sampleRecipient={recipients.length > 0 ? recipients[0] : null}
              />
            </Paper>
          </Box>
        )}

        {/* Tab 3: Review and Send */}
        {activeTab == 2 && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: ColorPalette.white,
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
                    border: `1px solid ${ColorPalette.border}`
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: ColorPalette.blueDark }}>
                      Destinatários Selecionados
                    </Typography>
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                      {selectedRecipients.length} contatos selecionados
                    </Typography>
                  </Box>
                  
                  <Box sx={{
                    backgroundColor: ColorPalette.white,
                    borderRadius: '8px',
                    overflow: 'auto',
                    height: '400px',
                    marginTop: '16px'
                  }}>
                    <RecipientList
                      recipients={recipients}
                      selectedRecipients={selectedRecipients}
                      onRecipientSelectionChange={handleRecipientSelectionChange}
                      onSelectAll={handleSelectAllRecipients}
                      onSendToRecipient={handleSendToRecipient}
                      messageTemplate={messageTemplate}
                      messageVariables={messageVariables}
                      sendingMethod={sendingMethod}
                    />
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <SendingSummary
                  selectedRecipients={selectedRecipients}
                  recipients={recipients}
                  messageTemplate={messageTemplate}
                  messageVariables={messageVariables}
                  sendingMethod={sendingMethod}
                  whatsappConfigured={whatsappSendingEnabled}
                  sending={sending}
                  handleSendToAll={handleSendToAll}
                  processMessageForRecipient={processMessageForRecipient}
                />
                
                <SendingMethodSelector
                  sendingMethod={sendingMethod}
                  onSendingMethodChange={handleSendingMethodChange}
                  whatsappConfigured={whatsappSendingEnabled}
                />
              </Grid>
            </Grid>
            
            <LoadingIndicator sending={sending} />
          </>
        )}

        {/* Navigation Buttons */}
        <NavigationButtons activeTab={activeTab} setActiveTab={setActiveTab} />
      </Paper>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        showConfirmPopup={showConfirmPopup}
        handleConfirmSend={handleConfirmSend}
        setShowConfirmPopup={setShowConfirmPopup}
      />

      {/* Results Dialog */}
      <ResultsDialog
        showResultsPopup={showResultsPopup}
        sendingResults={sendingResults}
        setShowResultsPopup={setShowResultsPopup}
      />

      {/* Erro de política do número WhatsApp (plataforma × próprio) */}
      <Snackbar
        open={!!sendPolicyError}
        autoHideDuration={8000}
        onClose={() => setSendPolicyError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSendPolicyError('')} severity="error" variant="filled">
          {sendPolicyError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MassMessaging;